// src/components/VaccineAdjustmentList.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  Heading,
  InputGroup,
  InputLeftElement,
  Select,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// สถานะที่หน้าจอใช้คือ ApprovalStatus เพราะบอกได้ทั้งขั้นตอนและผลลัพธ์ในคอลัมน์เดียว
const approvalStatusInfo = {
  Pending: { label: "รออนุมัติ", bg: "#FFF3E0", color: "#D97706" },
  Approved: { label: "ปรับยอดแล้ว", bg: "#D1FAE5", color: "#047857" },
  Rejected: { label: "ไม่อนุมัติ", bg: "#FEE2E2", color: "#991B1B" },
  Cancelled: { label: "ยกเลิก", bg: "#F3F4F6", color: "#6B7280" },
  Archived: { label: "เอกสารเก่า", bg: "#EDE9FE", color: "#5B21B6" },
};

const getApprovalStatusInfo = (status) =>
  approvalStatusInfo[status] || { label: status || "-", bg: "#EDF2F7", color: "#4A5568" };

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("th-TH");
};

const VaccineAdjustmentList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [adjustments, setAdjustments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isInventoryAdmin, setIsInventoryAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchWarehouse, setSearchWarehouse] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      width: "230px",
    },
    {
      name: "วันที่ขอปรับ",
      selector: (row) => row.RequestDate,
      format: (row) => formatDate(row.RequestDate),
      sortable: true,
      width: "130px",
    },
    {
      name: "คลัง",
      selector: (row) => row.WarehouseName || row.WarehouseID,
      sortable: true,
      wrap: true,
    },
    {
      name: "เหตุผลที่ขอปรับ",
      selector: (row) => row.RequestType || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "ผู้ยื่นคำขอ",
      selector: (row) => row.RequesterID || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "สถานะ",
      selector: (row) => row.ApprovalStatus,
      sortable: true,
      cell: (row) => {
        const info = getApprovalStatusInfo(row.ApprovalStatus);
        return (
          <Box
            bg={info.bg}
            color={info.color}
            borderColor={info.color}
            borderWidth={1}
            borderRadius="full"
            px={3}
            py={1}
            textAlign="center"
            fontSize="sm"
            fontWeight="semibold"
            display="inline-block"
          >
            {info.label}
          </Box>
        );
      },
      width: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccineadjustment/${row.TransactionID}`)}
          label={
            row.ApprovalStatus === "Pending" && isInventoryAdmin ? "ตรวจและยืนยัน" : "ดูรายละเอียด"
          }
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "160px",
    },
  ];

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineAdjustmentWarehouses",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  }, [sessionId]);

  const fetchAdjustments = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      await fetchWarehouses();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineAdjustmentsList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setAdjustments(response.VaccineAdjustmentsList || []);
        setIsInventoryAdmin(Boolean(response.IsInventoryAdmin));
      } else {
        setLoadError(response.error || "ไม่สามารถดึงข้อมูลได้");
      }
    } catch (error) {
      console.error("Error fetching adjustments:", error);
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, fetchWarehouses]);

  useEffect(() => {
    if (sessionId) {
      fetchAdjustments();
    }
  }, [sessionId, fetchAdjustments]);

  const filteredAdjustments = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    return adjustments.filter((row) => {
      if (searchWarehouse && row.WarehouseID !== searchWarehouse) {
        return false;
      }
      if (searchStatus && row.ApprovalStatus !== searchStatus) {
        return false;
      }
      if (!keyword) {
        return true;
      }
      return [row.TransactionID, row.RequestType, row.RequesterID, row.ReferenceDocumentNo]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword));
    });
  }, [adjustments, searchWarehouse, searchStatus, searchText]);

  const pendingCount = useMemo(
    () => adjustments.filter((row) => row.ApprovalStatus === "Pending").length,
    [adjustments]
  );

  const handleClearSearch = () => {
    setSearchWarehouse("");
    setSearchStatus("");
    setSearchText("");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={2}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการปรับยอดวัคซีนคงคลัง
            </Heading>
          </HStack>
        </Box>

        <HStack w="100%" spacing={3} flexWrap="wrap">
          {pendingCount > 0 && (
            <Badge colorScheme="orange" borderRadius="full" px={3} py={1} fontSize="sm">
              รออนุมัติ {pendingCount} ใบ
            </Badge>
          )}
          {isInventoryAdmin && (
            <Tooltip label="คุณมีสิทธิ์ตรวจและยืนยันการปรับยอดของทุกคลัง">
              <Badge colorScheme="purple" borderRadius="full" px={3} py={1} fontSize="sm">
                <HStack spacing={2}>
                  <FontAwesomeIcon icon={faShieldHalved} />
                  <span>ผู้ดูแลระบบคลังวัคซีน</span>
                </HStack>
              </Badge>
            </Tooltip>
          )}
        </HStack>

        <Text fontSize="sm" color="gray.600">
          การปรับยอดจะกันจำนวนที่ขอลดไว้ตั้งแต่ยื่นคำขอ จนกว่าผู้ดูแลระบบจะยืนยันหรือไม่อนุมัติ
          ระหว่างนั้นคลังยังเบิกจ่ายส่วนที่เหลือได้ตามปกติ
        </Text>

        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack
            w={{ base: "100%", md: "80%" }}
            spacing={4}
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <InputGroup w={{ base: "100%", md: "35%" }} mt={{ base: 4, md: 1 }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray" />
              </InputLeftElement>
              <Input
                placeholder="เลขที่เอกสาร เหตุผล หรือผู้ยื่น"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "35%" }}
              placeholder="ทุกคลัง"
              value={searchWarehouse}
              onChange={(e) => setSearchWarehouse(e.target.value)}
              colorScheme="iBlue"
              mt={{ base: 4, md: 1 }}
            >
              {warehouses.map((warehouse) => (
                <option key={warehouse.WarehouseID} value={warehouse.WarehouseID}>
                  {warehouse.WarehouseName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "30%" }}
              placeholder="ทุกสถานะ"
              value={searchStatus}
              onChange={(e) => setSearchStatus(e.target.value)}
              colorScheme="iBlue"
              mt={{ base: 4, md: 1 }}
            >
              {Object.entries(approvalStatusInfo).map(([value, info]) => (
                <option key={value} value={value}>
                  {info.label}
                </option>
              ))}
            </Select>
            <IconButton.Clear onClick={handleClearSearch} mt={{ base: 4, md: 0 }} />
          </HStack>
          <IconButton.Add
            onClick={() => navigate("/vaccineadjustment/new")}
            label="ยื่นคำขอปรับยอด"
            mt={{ base: 4, md: 0 }}
          />
        </HStack>

        {loadError ? (
          <Text fontSize="lg" color="red.500" textAlign="center" w="100%">
            {loadError}
          </Text>
        ) : isLoading ? (
          <Spinner size="md" />
        ) : adjustments.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลการปรับยอดวัคซีนคงคลัง
          </Text>
        ) : filteredAdjustments.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredAdjustments}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineAdjustmentList;
